import { Box } from "@mui/material";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { planIsActive, type PageBackgroundKind, type PageBackgroundPlan } from "@/utils/pageBackgrounds";
import type { BackgroundReleaseRef } from "./usePageBackgroundPlan";

const PageBackgroundCanvas = lazy(() =>
  import("./PageBackgroundCanvas").catch((error) => {
    console.error("[PageBackground] Failed to load the background renderer:", error);
    return { default: () => null };
  }),
);

class PageBackgroundBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error("[PageBackground] Background render failed:", error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export type PageBackgroundPlacement = "page" | "release" | "hero";

export interface PageBackgroundProps {
  plan: PageBackgroundPlan | null | undefined;
  kind: PageBackgroundKind;
  placement?: PageBackgroundPlacement;
  release?: BackgroundReleaseRef;
  coverUrl?: string | null;
  targetSelector?: string;
  targetRound?: boolean;
  seedKey?: string;
  delayMs?: number;
}

function whenIdle(callback: () => void, delayMs: number): () => void {
  let idleHandle: number | null = null;
  const timer = window.setTimeout(() => {
    if (typeof window.requestIdleCallback === "function") idleHandle = window.requestIdleCallback(callback, { timeout: 1500 });
    else callback();
  }, delayMs);
  return () => {
    window.clearTimeout(timer);
    if (idleHandle !== null && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleHandle);
  };
}

export function PageBackground({ plan, kind, placement = "page", delayMs = 700, ...rest }: PageBackgroundProps) {
  const active = planIsActive(plan);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!active) return;
    return whenIdle(() => setReady(true), delayMs);
  }, [active, delayMs]);

  if (!active || !plan) return null;

  const layer = (
    <Box
      className="PageBackground"
      aria-hidden="true"
      data-page-background={plan.style}
      sx={{
        position: placement === "hero" ? "absolute" : "fixed",
        inset: 0,
        zIndex: placement === "page" ? -1 : placement === "hero" ? 0 : undefined,
        pointerEvents: "none",
        overflow: "hidden",
        contain: "strict",
        backgroundColor: placement === "page" ? "var(--backgroundColor)" : "transparent",
        ...(placement === "hero"
          ? {
              WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 72%, rgba(0,0,0,0.5) 88%, transparent 100%)",
              maskImage: "linear-gradient(to bottom, #000 0%, #000 72%, rgba(0,0,0,0.5) 88%, transparent 100%)",
            }
          : {}),
      }}
    >
      {ready ? (
        <PageBackgroundBoundary>
          <Suspense fallback={null}>
            <PageBackgroundCanvas plan={plan} kind={kind} placement={placement} {...rest} />
          </Suspense>
        </PageBackgroundBoundary>
      ) : null}
    </Box>
  );

  if (placement === "page" && typeof document !== "undefined") return createPortal(layer, document.body);
  return layer;
}

export default PageBackground;
